import { ReactNode } from "react";
import { ArrowRight, ChevronDown, Phone } from "lucide-react";
import { cn } from "@/lib/utils";
import { useReveal } from "@/hooks/use-reveal";

/* ----------------------------- Reveal wrapper ----------------------------- */

export const Reveal = ({
  children,
  delay = 0,
  className,
  as: As = "div",
}: {
  children: ReactNode;
  delay?: number;
  className?: string;
  as?: any;
}) => {
  const { ref, visible } = useReveal<HTMLDivElement>();
  return (
    <As
      ref={ref}
      style={{ animationDelay: visible ? `${delay}ms` : undefined }}
      className={cn(
        "opacity-0",
        visible && "animate-reveal-up",
        className,
      )}
    >
      {children}
    </As>
  );
};

/* --------------------------------- HERO ---------------------------------- */

export const CinematicHero = ({
  eyebrow,
  chapter,
  title,
  subtitle,
  image,
  imageAlt,
  ctaLabel = "Kostenloses Angebot anfordern",
  ctaHref = "/kontakt",
  phone = "05221 / 3832130",
  compact = false,
}: {
  eyebrow: string;
  chapter: string;
  title: ReactNode;
  subtitle: string;
  image: string;
  imageAlt: string;
  ctaLabel?: string;
  ctaHref?: string;
  phone?: string;
  compact?: boolean;
}) => {
  return (
    <section className={cn(
      "relative w-full overflow-hidden bg-foreground text-background",
      compact ? "h-[50svh] min-h-[360px]" : "h-[100svh] min-h-[640px]"
    )}>
      <div className="absolute inset-0">
        <img
          src={image}
          alt={imageAlt}
          className="w-full h-full object-cover animate-ken-burns will-change-transform"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-foreground via-foreground/70 to-foreground/30" />
        <div className="absolute inset-0 bg-gradient-to-r from-foreground/60 via-transparent to-transparent" />
      </div>

      {/* Chapter marker top right */}
      <div className="absolute top-28 md:top-32 right-6 md:right-12 z-10 text-right">
        <div className="text-[10px] tracking-[0.35em] uppercase text-background/75 mb-2">
          {chapter}
        </div>
        <div className="h-px w-16 bg-background/30 ml-auto" />
      </div>

      <div className="absolute inset-0 z-10 flex items-end">
        <div className={cn("container mx-auto", compact ? "pb-10 md:pb-14" : "pb-20 md:pb-28")}>
          <div className="max-w-4xl animate-reveal-blur">
            <div className="inline-flex items-center gap-3 mb-8">
              <span className="h-px w-10 bg-background/50" />
              <span className="text-[10px] tracking-[0.3em] uppercase text-background/70">
                {eyebrow}
              </span>
            </div>
            <h1 className={cn(
              "font-caslon font-bold tracking-tight mb-8 text-background",
              compact
                ? "text-[28px] leading-[1.05] md:text-4xl lg:text-5xl"
                : "text-[42px] leading-[1.02] md:text-7xl lg:text-[88px]"
            )}>
              {title}
            </h1>
            <p className={cn(
              "max-w-2xl text-background/75 leading-relaxed font-light",
              compact ? "text-sm md:text-base mb-6" : "text-base md:text-lg mb-10"
            )}>
              {subtitle}
            </p>
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-5">
              <a
                href={ctaHref}
                className="group inline-flex items-center gap-3 px-7 py-4 bg-background text-foreground font-medium rounded-full text-sm hover:bg-background/90 transition-all hover:translate-y-[-2px]"
              >
                {ctaLabel}
                <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
              </a>
              <a
                href={`tel:+49${phone.replace(/\D/g, "").replace(/^0/, "")}`}
                className="inline-flex items-center gap-2 text-sm text-background/70 hover:text-background transition-colors"
              >
                <Phone className="w-4 h-4" />
                {phone}
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Scroll cue */}
      {!compact && <div className="absolute bottom-6 left-1/2 -translate-x-1/2 z-10 flex flex-col items-center gap-2 text-background/80">
        <span className="text-[10px] tracking-[0.3em] uppercase">Scroll</span>
        <ChevronDown className="w-4 h-4 animate-float" />
      </div>}
    </section>
  );
};

/* ------------------------------ Section frame ----------------------------- */

export const SectionHead = ({
  eyebrow,
  title,
  intro,
  align = "left",
  invert = false,
}: {
  eyebrow?: string;
  title: ReactNode;
  intro?: ReactNode;
  align?: "left" | "center";
  invert?: boolean;
}) => (
  <div
    className={cn(
      "max-w-3xl mb-16 md:mb-20",
      align === "center" && "mx-auto text-center",
    )}
  >
    {eyebrow && (
      <Reveal>
        <div
          className={cn(
            "inline-flex items-center gap-3 mb-6",
            align === "center" && "justify-center",
          )}
        >
          <span className={cn("h-px w-8", invert ? "bg-background/40" : "bg-foreground/30")} />
          <span
            className={cn(
              "text-[10px] tracking-[0.3em] uppercase",
              invert ? "text-background/85" : "text-muted-foreground",
            )}
          >
            {eyebrow}
          </span>
        </div>
      </Reveal>
    )}
    <Reveal delay={80}>
      <h2
        className={cn(
          "font-caslon font-bold tracking-tight text-3xl md:text-5xl lg:text-6xl leading-[1.05]",
          invert ? "text-background" : "text-foreground",
        )}
      >
        {title}
      </h2>
    </Reveal>
    {intro && (
      <Reveal delay={180}>
        <p
          className={cn(
            "mt-6 text-base md:text-lg leading-relaxed max-w-2xl",
            align === "center" && "mx-auto",
            invert ? "text-background/70" : "text-muted-foreground",
          )}
        >
          {intro}
        </p>
      </Reveal>
    )}
  </div>
);

/* ----------------------------- Full-bleed band ---------------------------- */

export const FullBleedQuote = ({
  image,
  imageAlt,
  quote,
  caption,
  height = "tall",
}: {
  image: string;
  imageAlt: string;
  quote: ReactNode;
  caption?: string;
  height?: "tall" | "medium";
}) => {
  const { ref, visible } = useReveal<HTMLElement>();
  return (
    <section
      ref={ref}
      className={cn(
        "relative w-full overflow-hidden bg-foreground text-background",
        height === "tall" ? "h-[85vh] min-h-[560px]" : "h-[60vh] min-h-[420px]",
      )}
    >
      <img
        src={image}
        alt={imageAlt}
        className={cn(
          "absolute inset-0 w-full h-full object-cover transition-transform duration-[20s] ease-out",
          visible ? "scale-105" : "scale-100",
        )}
      />
      <div className="absolute inset-0 bg-foreground/55" />
      <div className="absolute inset-0 bg-gradient-to-t from-foreground/80 to-transparent" />
      <div className="relative z-10 h-full container mx-auto flex flex-col justify-end pb-16 md:pb-24">
        <div className={cn("max-w-4xl", visible && "animate-reveal-blur opacity-0")}>
          <p className="font-caslon text-2xl md:text-4xl lg:text-5xl leading-[1.15] font-medium text-background">
            {quote}
          </p>
          {caption && (
            <p className="mt-6 text-[11px] tracking-[0.3em] uppercase text-background/85">
              {caption}
            </p>
          )}
        </div>
      </div>
    </section>
  );
};

/* ------------------------------ Split feature ----------------------------- */

export const SplitFeature = ({
  index,
  eyebrow,
  title,
  body,
  image,
  imageAlt,
  reverse = false,
  bullets,
}: {
  index?: string;
  eyebrow?: string;
  title: ReactNode;
  body: ReactNode;
  image: string;
  imageAlt: string;
  reverse?: boolean;
  bullets?: string[];
}) => {
  return (
    <div className="grid lg:grid-cols-12 gap-8 lg:gap-16 items-center">
      <Reveal
        className={cn(
          "lg:col-span-7 relative aspect-[4/5] md:aspect-[5/4] overflow-hidden rounded-2xl bg-muted",
          reverse && "lg:order-2",
        )}
      >
        <img
          src={image}
          alt={imageAlt}
          loading="lazy"
          className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1.6s] hover:scale-105"
        />
      </Reveal>
      <div className={cn("lg:col-span-5", reverse && "lg:order-1")}>
        {index && (
          <Reveal>
            <div className="font-caslon text-5xl text-foreground/15 mb-6">{index}</div>
          </Reveal>
        )}
        {eyebrow && (
          <Reveal delay={60}>
            <div className="text-[10px] tracking-[0.3em] uppercase text-muted-foreground mb-4">
              {eyebrow}
            </div>
          </Reveal>
        )}
        <Reveal delay={120}>
          <h3 className="font-caslon text-2xl md:text-4xl font-bold leading-tight mb-5">
            {title}
          </h3>
        </Reveal>
        <Reveal delay={200}>
          <div className="text-muted-foreground leading-relaxed text-base">{body}</div>
        </Reveal>
        {bullets && (
          <Reveal delay={260}>
            <ul className="mt-6 space-y-2">
              {bullets.map((b) => (
                <li key={b} className="flex items-start gap-3 text-sm text-foreground/80">
                  <span className="mt-2 h-px w-4 bg-foreground/40 shrink-0" />
                  <span>{b}</span>
                </li>
              ))}
            </ul>
          </Reveal>
        )}
      </div>
    </div>
  );
};

/* ----------------------------- Numbered Steps ----------------------------- */

export const NumberedSteps = ({
  steps,
  invert = false,
}: {
  steps: { title: string; desc: string }[];
  invert?: boolean;
}) => (
  <div className="grid md:grid-cols-2 gap-x-12 gap-y-12 md:gap-y-16">
    {steps.map((s, i) => (
      <Reveal key={i} delay={i * 80}>
        <div className="group">
          <div
            className={cn(
              "flex items-baseline gap-5 mb-4 border-b pb-4",
              invert ? "border-background/15" : "border-foreground/10",
            )}
          >
            <span
              className={cn(
                "font-caslon text-3xl md:text-4xl lg:text-5xl font-bold",
                invert ? "text-background/30" : "text-foreground/20",
              )}
            >
              {String(i + 1).padStart(2, "0")}
            </span>
            <h3
              className={cn(
                "font-semibold text-lg md:text-xl",
                invert ? "text-background" : "text-foreground",
              )}
            >
              {s.title}
            </h3>
          </div>
          <p
            className={cn(
              "text-sm leading-relaxed pl-[3.75rem]",
              invert ? "text-background/65" : "text-muted-foreground",
            )}
          >
            {s.desc}
          </p>
        </div>
      </Reveal>
    ))}
  </div>
);

/* -------------------------------- Stats row ------------------------------- */

export const StatStripe = ({
  stats,
}: {
  stats: { value: string; label: string }[];
}) => (
  <section className="bg-background border-y border-foreground/10">
    <div className="container mx-auto">
      <div className="grid grid-cols-2 md:grid-cols-4 divide-x divide-foreground/10">
        {stats.map((s, i) => (
          <Reveal key={s.label} delay={i * 80} className="py-12 md:py-16 px-6 text-center">
            <div className="font-caslon text-4xl md:text-6xl font-bold text-foreground">
              {s.value}
            </div>
            <div className="text-[10px] tracking-[0.25em] uppercase text-muted-foreground mt-3">
              {s.label}
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

/* --------------------------------- CTA dark ------------------------------- */

export const CinematicCTA = ({
  image,
  imageAlt,
  title,
  body,
  ctaLabel = "Beratung anfragen",
  ctaHref = "/kontakt",
  phone = "05221 / 3832130",
}: {
  image: string;
  imageAlt: string;
  title: ReactNode;
  body?: string;
  ctaLabel?: string;
  ctaHref?: string;
  phone?: string;
}) => (
  <section className="relative w-full overflow-hidden bg-foreground text-background py-32 md:py-48">
    <img
      src={image}
      alt={imageAlt}
      className="absolute inset-0 w-full h-full object-cover opacity-40"
    />
    <div className="absolute inset-0 bg-gradient-to-b from-foreground/80 via-foreground/60 to-foreground" />
    <div className="relative container mx-auto text-center max-w-3xl">
      <Reveal>
        <h2 className="font-caslon text-4xl md:text-6xl lg:text-7xl font-bold leading-[1.05] mb-6">
          {title}
        </h2>
      </Reveal>
      {body && (
        <Reveal delay={120}>
          <p className="text-background/70 text-base md:text-lg mb-10 max-w-xl mx-auto leading-relaxed">
            {body}
          </p>
        </Reveal>
      )}
      <Reveal delay={200}>
        <div className="flex flex-col sm:flex-row items-center justify-center gap-5">
          <a
            href={ctaHref}
            className="group inline-flex items-center gap-3 px-8 py-4 bg-background text-foreground font-medium rounded-full text-sm hover:bg-background/90 transition-all hover:translate-y-[-2px]"
          >
            {ctaLabel}
            <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
          </a>
          <a
            href={`tel:+49${phone.replace(/\D/g, "").replace(/^0/, "")}`}
            className="inline-flex items-center gap-2 text-sm text-background/70 hover:text-background transition-colors"
          >
            <Phone className="w-4 h-4" />
            {phone}
          </a>
        </div>
      </Reveal>
    </div>
  </section>
);

/* --------------------------- FAQ – cinematic UI --------------------------- */

export const CinematicFAQ = ({
  faqs,
  eyebrow = "Häufige Fragen",
  title,
}: {
  faqs: { q: string; a: string }[];
  eyebrow?: string;
  title: string;
}) => (
  <section className="py-16 md:py-24 lg:py-32 bg-background">
    <div className="container mx-auto max-w-4xl">
      <SectionHead eyebrow={eyebrow} title={title} />
      <div className="divide-y divide-foreground/10 border-t border-foreground/10">
        {faqs.map((f, i) => (
          <Reveal key={i} delay={i * 60}>
            <details className="group py-6 md:py-8">
              <summary className="flex items-start gap-6 cursor-pointer list-none">
                <span className="font-caslon text-2xl md:text-3xl text-foreground/25 shrink-0 w-12">
                  {String(i + 1).padStart(2, "0")}
                </span>
                <span className="flex-1 font-caslon text-lg md:text-2xl font-medium text-foreground leading-snug">
                  {f.q}
                </span>
                <span className="text-foreground/40 text-2xl transition-transform group-open:rotate-45 shrink-0 mt-1">
                  +
                </span>
              </summary>
              <div className="pt-5 pl-[4.5rem] pr-12 text-muted-foreground leading-relaxed text-sm md:text-base">
                {f.a}
              </div>
            </details>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

/* --------------------------- Gallery strip (3 imgs) ----------------------- */

export const TripleStrip = ({
  images,
}: {
  images: { src: string; alt: string; caption?: string }[];
}) => (
  <section className="bg-background">
    <div className="grid grid-cols-1 md:grid-cols-3 gap-0">
      {images.map((img, i) => (
        <Reveal key={i} delay={i * 100} className="relative aspect-[4/5] overflow-hidden group">
          <img
            src={img.src}
            alt={img.alt}
            loading="lazy"
            className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1.8s] group-hover:scale-110"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-foreground/70 via-foreground/10 to-transparent opacity-90" />
          {img.caption && (
            <div className="absolute bottom-0 left-0 right-0 p-6 md:p-8">
              <div className="text-[10px] tracking-[0.3em] uppercase text-background/70 mb-1">
                {String(i + 1).padStart(2, "0")} / Referenz
              </div>
              <div className="font-caslon text-xl md:text-2xl text-background">
                {img.caption}
              </div>
            </div>
          )}
        </Reveal>
      ))}
    </div>
  </section>
);